import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { TableColumnsProps } from "@lib/types/General";
import { XAILead } from "@lib/types/xAI";
import { Tooltip } from "antd";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import {
  XAICallStatusTag,
  LanguageLabel,
} from "@lib/components/MiniComponents/Others";

type Props<T> = TableColumnsProps<T> & {
  handleSelect: (item: XAILead) => void;
  handleDelete: (item: XAILead) => void;
  handleResend: (item: XAILead) => void;
};

const getColumns = <T extends XAILead>(props: Props<T>): ColumnsType<T> => {
  const { handleSelect, handleDelete, handleResend, detailContext } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (v, r) => (
        <TableIdButton text={`#${shortenString(r.id)}`} textToBeCopied={r.id} />
      ),
      width: "180px",
      filterType: "search",
    },

    {
      title: "common.customer",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "150px",
      render: (_, r) => <CustomerName customer={r?.customer} link />,
      hidden: !!detailContext,
    },

    {
      title: "common.firstName",
      dataIndex: "firstName",
      key: "firstName",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.lastName",
      dataIndex: "lastName",
      key: "lastName",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.language",
      dataIndex: "language",
      key: "language",
      width: "150px",
      render: (d) => <LanguageLabel languageCode={d} />,
    },

    {
      title: "common.agent",
      dataIndex: "agent",
      key: "agent",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.preferredPhoneNumber",
      dataIndex: "extraData.preferredPhoneNumber",
      key: "extraData.preferredPhoneNumber",
      width: "150px",
      filterType: "search",
      render: (_, r) => r?.extraData?.preferredPhoneNumber || "-",
    },

    {
      title: "common.email",
      dataIndex: "email",
      key: "email",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.aiModelId",
      dataIndex: "extraData.aiModelId",
      key: "extraData.aiModelId",
      width: "150px",
      filterType: "search",
      render: (_, r) => r?.extraData?.aiModelId || "-",
    },

    {
      title: "common.brokerName",
      dataIndex: "extraData.brokerName",
      key: "extraData.brokerName",
      width: "150px",
      filterType: "search",
      render: (_, r) => r?.extraData?.brokerName || "-",
    },

    {
      title: "common.brand",
      dataIndex: "brand",
      key: "brand",
      width: "250px",
      filterType: "search",
    },
    {
      title: "common.businessUnit",
      dataIndex: "businessUnit",
      key: "businessUnit",
      width: "250px",
      filterType: "search",
    },

    {
      title: "common.lastVisit",
      dataIndex: "lastVisit",
      key: "lastVisit",
      width: "150px",
    },

    {
      title: "common.status",
      dataIndex: "status",
      key: "status",
      width: "150px",
      filterType: "dropdown",
    },

    {
      title: "common.callStatus",
      dataIndex: "callStatus",
      key: "callStatus",
      width: "150px",
      filterType: "dropdown",
      render: (_, r) =>
        r?.callStatus ? (
          <XAICallStatusTag status={r.callStatus} size="small" />
        ) : (
          "-"
        ),
    },

    {
      title: "common.summary",
      dataIndex: "summary",
      key: "summary",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.talkingPoints",
      dataIndex: "talkingPoints",
      key: "talkingPoints",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.error",
      dataIndex: "error",
      key: "error",
      width: "250px",
      filterType: "search",
      render: (d) => <Tooltip title={d}>{shortenString(d, 20)}</Tooltip>,
    },

    {
      title: "common.note",
      dataIndex: "note",
      key: "note",
      width: "250px",
      filterType: "search",
      render: (d) => <Tooltip title={d}>{shortenString(d, 20)}</Tooltip>,
    },

    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      key: "createdBy",
      width: "150px",
      filterType: "dropdown",
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "common.updatedBy",
      dataIndex: "updatedBy",
      width: "160px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "180px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelect(record)}
            noLabel
          />
          <ActionButton
            type="refresh"
            label="Resend"
            onClick={() => handleResend(record)}
            noLabel
          />
          <ActionButton
            type="delete"
            onClick={() => handleDelete(record)}
            itemName="sidebar.tftSalesLeads"
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
